/**
 * 基于 localStorage 的 storage 工具，替代 uni.getStorageSync / uni.setStorageSync
 *
 */
type StorageWrapped =
	| { type: 'object'; data: unknown }
	| { type: 'number'; data: number }
	| { type: 'boolean'; data: boolean }
	| { type: 'null'; data: null };

function safe() {
	return typeof window !== 'undefined';
}

function isWrapped(v: unknown): v is StorageWrapped {
	if (!v || typeof v !== 'object') return false;
	const t = (v as any).type;
	return (t === 'object' || t === 'number' || t === 'boolean' || t === 'null') && 'data' in (v as any);
}

export function getStorageSync<T = unknown>(key: string): T | string | null {
	if (!safe()) return null;
	const v = localStorage.getItem(key);
	if (!v) return null;

	// 字符串：原样返回，不做 JSON.parse（避免 "123"/"true" 这类字符串被误解析）
	const trimmed = v.trim();
	if (!trimmed.startsWith('{')) return v;

	try {
		const parsed = JSON.parse(v) as unknown;
		return isWrapped(parsed) ? ((parsed as StorageWrapped).data as T) : v;
	} catch {
		return v;
	}
}

export function setStorageSync(key: string, data: unknown) {
	if (!safe()) return;
	// 字符串：按原样存取（兼容历史值）
	if (typeof data === 'string') {
		localStorage.setItem(key, data);
		return;
	}
	// undefined：按 uniapp 习惯直接移除
	if (typeof data === 'undefined') {
		localStorage.removeItem(key);
		return;
	}
	// 对象/数组：统一包装为 { type: "object", data: ... }
	if (data !== null && typeof data === 'object') {
		localStorage.setItem(key, JSON.stringify({ type: 'object', data } satisfies StorageWrapped));
		return;
	}

	// number / boolean / null：也做 type 包装，读取时可还原
	if (typeof data === 'number') {
		localStorage.setItem(key, JSON.stringify({ type: 'number', data } satisfies StorageWrapped));
		return;
	}
	if (typeof data === 'boolean') {
		localStorage.setItem(key, JSON.stringify({ type: 'boolean', data } satisfies StorageWrapped));
		return;
	}
	if (data === null) {
		localStorage.setItem(key, JSON.stringify({ type: 'null', data: null } satisfies StorageWrapped));
		return;
	}

	// 兜底：极少见类型（symbol/function/bigint），转字符串存
	localStorage.setItem(key, String(data));
}

export function removeStorageSync(key: string) {
	if (!safe()) return;
	localStorage.removeItem(key);
}

export function clearStorageSync() {
	if (!safe()) return;
	localStorage.clear();
}
